import { useEffect, useState } from 'react';

import type { StorefrontItem } from '../api/types.ts';
import { useStore } from '../core/store.ts';
import { errorText, useStrings, type Strings } from '../i18n/index.ts';
import { cartStore, qtyOf } from '../state/cart.ts';
import { homeStore } from '../state/home.ts';
import { useProductList } from '../state/product-list.ts';
import { searchQueryStore } from '../state/search.ts';
import { EmptyState, ErrorBanner, SectionHeader } from '../ui/kit.tsx';
import { ProductGrid, ProductGridSkeleton, ProductLane } from '../ui/ProductTile.tsx';
import { Screen } from '../ui/Screen.tsx';
import '../styles/storefront.css';

const DEBOUNCE_MS = 300;

/** Поиск по всему складу: поле ввода стоит в нижней панели на месте вкладок. */
export function SearchScreen() {
  const strings = useStrings();
  const query = useStore(searchQueryStore).trim();
  const home = useStore(homeStore);
  const cart = useStore(cartStore);

  // Запрос приходит по букве: список перечитывается после паузы во вводе.
  const [settled, setSettled] = useState(query);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(query), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]);
  const list = useProductList(settled ? { kind: 'search', text: settled } : null);

  // Покупки без того, что уже в корзине: данные главной, своих запросов поиск не делает.
  const bought = home.purchases.filter((item) => qtyOf(cart, item.id) === 0);
  const collections = home.feed?.collections ?? [];
  const suggestions = <Suggestions strings={strings} bought={bought} collections={collections} />;

  return (
    <Screen className="search-screen">
      <div className="page stack" style={{ gap: 16 }}>
        {query === '' ? (
          bought.length === 0 && collections.length === 0 ? (
            <div style={{ paddingTop: 40 }}>
              <EmptyState icon="search" title={strings.searchEmptyTitle} hint={strings.searchHint} />
            </div>
          ) : (
            <div style={{ paddingTop: 8 }}>{suggestions}</div>
          )
        ) : list.error ? (
          <div className="gutter" style={{ paddingTop: 12 }}>
            <ErrorBanner text={errorText(strings, list.error)} onRetry={list.reload} />
          </div>
        ) : list.isLoading || settled !== query ? (
          list.items.length > 0 ? (
            <Results strings={strings} items={list.items} />
          ) : (
            <div style={{ paddingTop: 12 }}>
              <ProductGridSkeleton />
            </div>
          )
        ) : list.items.length === 0 ? (
          <>
            <div style={{ paddingTop: 24 }}>
              <EmptyState icon="search" title={strings.nothingFound} hint={strings.nothingFoundHint} />
            </div>
            <div style={{ paddingTop: 8 }}>{suggestions}</div>
          </>
        ) : (
          <>
            <Results strings={strings} items={list.items} />
            <div style={{ paddingTop: 16 }}>{suggestions}</div>
          </>
        )}
      </div>
    </Screen>
  );
}

function Results({ strings, items }: { strings: Strings; items: StorefrontItem[] }) {
  return (
    <>
      <h2 className="t-display gutter" style={{ fontSize: 22, paddingTop: 12 }}>
        {strings.resultsCount(items.length)}
      </h2>
      <ProductGrid items={items} />
    </>
  );
}

/** Под пустым полем поиска и под результатами: покупки сеткой по три в ряд, дальше ленты подборок. */
function Suggestions(props: { strings: Strings; bought: StorefrontItem[]; collections: { id: string; title: string; items: StorefrontItem[] }[] }) {
  return (
    <div className="stack" style={{ gap: 26 }}>
      {props.bought.length > 0 && (
        <section className="stack" style={{ gap: 12 }}>
          <SectionHeader title={props.strings.youBought} />
          <ProductGrid items={props.bought} dense />
        </section>
      )}
      {props.collections.map((collection) => (
        <section key={collection.id} className="stack" style={{ gap: 12 }}>
          <SectionHeader title={collection.title} />
          <ProductLane items={collection.items} />
        </section>
      ))}
    </div>
  );
}
